import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

import { hashScrollTarget, shouldScrollToTop, type ScrollNavigationType } from "./scroll-to-top-policy";


// 锚点目标常常是懒加载/数据到达后才挂上的（客户详情的工作台就是），所以边等边试；
// 上限约 2.4 秒，始终找不到就交给调用方回落。
const HASH_SCROLL_RETRY_MS = 60;
const HASH_SCROLL_MAX_ATTEMPTS = 40;


/** 等锚点元素出现再滚过去。返回清理函数。 */
export function scrollToHashWhenReady(target: string, onGiveUp: () => void): () => void {
  let attempts = 0;
  let timer: number | undefined;
  const attemptScroll = () => {
    const element = document.getElementById(target);
    if (element) {
      element.scrollIntoView({ block: "start", behavior: "instant" });
      return;
    }
    attempts += 1;
    if (attempts < HASH_SCROLL_MAX_ATTEMPTS) {
      timer = window.setTimeout(attemptScroll, HASH_SCROLL_RETRY_MS);
      return;
    }
    onGiveUp();
  };
  attemptScroll();
  return () => {
    if (timer !== undefined) window.clearTimeout(timer);
  };
}


export function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const navigationType = useNavigationType() as ScrollNavigationType;
  const previousPathname = useRef<string | null>(null);

  // 直接打开 / 刷新一个带 # 的地址：react-router 给首次加载的动作也是 POP（页面还没有位置可保），
  // 但用户要的就是那个锚点。单独一个 mount effect 处理，不跟后面的导航策略纠缠。
  // StrictMode 会把 effect 跑两遍，两条重试链各自幂等，不影响结果。
  useEffect(() => {
    const target = hashScrollTarget(window.location.hash);
    if (!target) return;
    return scrollToHashWhenReady(target, () => {});
  }, []);

  useEffect(() => {
    // 回落判定要在改写 previousPathname 之前算：以前写在重试里，等 2.4 秒后那时
    // previous 已经等于当前 pathname，判断恒为 false——回落成了死代码（2026-10-07 复核）。
    const fallbackToTop = shouldScrollToTop(previousPathname.current, pathname, navigationType);
    previousPathname.current = pathname;
    // 后退（POP）不抢浏览器恢复的位置；前进/替换到带 # 的地址才滚锚点。
    const target = navigationType === "POP" ? null : hashScrollTarget(hash);
    if (target) {
      return scrollToHashWhenReady(target, () => {
        if (fallbackToTop) window.scrollTo({ top: 0, behavior: "instant" });
      });
    }
    if (fallbackToTop) {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [navigationType, pathname, hash]);

  return null;
}
